import { AuthApiService, NavigationService, OidcService } from '@revenuewell/front-end-bundle';
import { Appointment, AppointmentProcedure, AppointmentRequestStatuses, AppointmentSlotItem, AppointmentStatus, PatientAppointment } from './types';
import { AppointmentAvailability } from './types/appointment-availability';
import { AppointmentModifications } from './types/appointment-modifications';
import { AppointmentRequestDetails } from './types/appointment-request-details';
import { AppointmentRequestNote } from './types/appointment-request-note';
import { AppointmentRequestSummary } from './types/appointment-request-summary';
import { AppointmentRequestsQuery } from './types/appointment-requests-query';
import { CancellationAppointment } from './types/cancellation-appointment';

export default class AppointmentService extends AuthApiService {
  private serviceUrl: string;

  public constructor(config: any, oidcService: OidcService) {
    super(oidcService, new NavigationService());
    this.serviceUrl = config.appointment.apiUrl;
  }

  public async getAppointmentProcedures(appointmentId: number, locationId: number): Promise<AppointmentProcedure[]> {
    const url = `${this.serviceUrl}/appointments/${appointmentId}/procedures?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async getPatientAppointments(patientId: number, locationId: number): Promise<PatientAppointment[]> {
    const url = `${this.serviceUrl}/patients/${patientId}/appointments?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async getAppointmentSlots(
    locationId: number,
    startDate: string,
    endDate: string
  ): Promise<AppointmentSlotItem[]> {
    const url = `${this.serviceUrl}/locations/${locationId}/appointments/${startDate}/${endDate}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async getAppointmentById(appointmentId: number, locationId: number): Promise<Appointment> {
    const url = `${this.serviceUrl}/appointments/${appointmentId}?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async updateAppointmentStatus(
    appointmentId: number,
    locationId: number,
    status: AppointmentStatus
  ): Promise<void> {
    const url = `${this.serviceUrl}/appointments/${appointmentId}/changestatus?locationId=${locationId}`;

    await this.authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    });
  }

  public async getAppointmentRequests(
    locationId: number,
    query: AppointmentRequestsQuery
  ): Promise<AppointmentRequestSummary[]> {
    let url = `${this.serviceUrl}/appointment-requests`;
    const params = new URLSearchParams();

    params.append('locationId', locationId.toString());

    // Handle array-based filters properly
    if (query.requestStatusFilter && query.requestStatusFilter.length > 0) {
      query.requestStatusFilter.forEach((status) => {
        params.append('requestStatusFilter', status);
      });
    }

    if (query.requestTypeFilter && query.requestTypeFilter.length > 0) {
      query.requestTypeFilter.forEach((type) => {
        params.append('requestTypeFilter', type);
      });
    }

    if (query.requestSourceFilter && query.requestSourceFilter.length > 0) {
      query.requestSourceFilter.forEach((source) => {
        params.append('requestSourceFilter', source);
      });
    }

    // Handle other non-array parameters
    if (query.lastDate !== undefined) {
      params.append('lastDate', query.lastDate);
    }

    if (query.numberOfRecords !== undefined) {
      params.append('numberOfRecords', query.numberOfRecords.toString());
    }

    url += `?${params.toString()}`;

    const response = await this.authFetch(url);

    return await response.json();
  }

  public async getAppointmentRequestDetails(
    apptRequestId: number,
    locationId: number
  ): Promise<AppointmentRequestDetails> {
    const url = `${this.serviceUrl}/appointment-requests/${apptRequestId}?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async getAppointmentAvailability(
    locationId: number,
    apptRequestId: number
  ): Promise<AppointmentAvailability[]> {
    const url = `${this.serviceUrl}/appointment-requests/${apptRequestId}/availability?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async getAppointmentRequestNotes(
    appointmentRequestId: number,
    locationId: number
  ): Promise<AppointmentRequestNote[]> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/notes?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async updateAppointmentRequestNotes(
    appointmentRequestId: number,
    locationId: number,
    notes: AppointmentRequestNote[]
  ): Promise<void> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/notes?locationId=${locationId}`;

    await this.authFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(notes)
    });
  }

  public async dismissAppointmentRequest(
    appointmentRequestId: number,
    locationId: number,
    masterAccountId?: number
  ): Promise<void> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/dismiss?locationId=${locationId}&masterAccountId=${masterAccountId}`;

    await this.authFetch(url, { method: 'PUT' });
  }

  public async acceptRescheduleAppointment(appointmentRequestId: number, locationId: number): Promise<void> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/reschedule/accept?locationId=${locationId}`;

    await this.authFetch(url, {
      method: 'POST'
    });
  }

  public async acceptCancelAppointment(appointmentRequestId: number, locationId: number): Promise<void> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/cancel/accept?locationId=${locationId}`;

    await this.authFetch(url, {
      method: 'POST'
    });
  }

  public async getAppointmentModifications(
    appointmentRequestId: number,
    locationId: number
  ): Promise<AppointmentModifications> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/modifications?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async getCancellationAppointment(
    appointmentRequestId: number,
    locationId: number
  ): Promise<CancellationAppointment> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/cancellation-appointment?locationId=${locationId}`;
    const response = await this.authFetch(url);

    return response.json();
  }

  public async markAsRead(appointmentRequestId: number, locationId: number): Promise<void> {
    const url = `${this.serviceUrl}/appointment-requests/${appointmentRequestId}/mark-as-read?locationId=${locationId}`;
    await this.authFetch(url, {
      method: 'PUT'
    });
  }

  public async getAppointmentRequestStatuses(locationId: number, appointmentRequestIds: number[]): Promise<AppointmentRequestStatuses[]> {
    const url = `${this.serviceUrl}/appointment-requests/statuses?locationId=${locationId}`;
    const response = await this.authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ appointmentRequestIds }),
    });

    return response.json();
  }
}
